import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X,
  Loader2,
  Trophy,
  Zap,
  AlertTriangle,
  CheckCircle2,
  Info,
  Plane,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils';
import {
  compararAirCotizaciones,
  selectAirCotizacion,
} from '../airCotizaciones.service.js';
import { formatImporte } from '../utils/airCotizacionCalc.js';

const MONEDAS = ['USD', 'COP', 'EUR', 'MXN'];

/**
 * Comparación lado a lado de las cotizaciones de una solicitud.
 *
 * Lo que hace útil a esta vista no es el total, sino lo que falta: un
 * proveedor que no cotizó el THC destino se ve más barato de lo que es.
 * Por eso una celda vacía dice **"no cotizó"** y nunca 0, y la cabecera
 * de cada columna lleva la cuenta de conceptos no cotizados.
 *
 * Cuando hay ofertas en otra moneda y no se cargó la tasa, esa columna
 * queda marcada como no comparable en vez de convertirse con un número
 * inventado.
 */
export default function ComparadorCotizacionesAereoDialog({
  open,
  onOpenChange,
  airRequest,
  onChanged,
}) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [monedaComparacion, setMonedaComparacion] = useState('USD');
  const [tasas, setTasas] = useState({});
  const [excluidas, setExcluidas] = useState(() => new Set());
  const [soloDiferencias, setSoloDiferencias] = useState(false);

  const cargar = useCallback(
    async (opts = {}) => {
      if (!airRequest?._id) return;
      setCargando(true);
      try {
        const r = await compararAirCotizaciones(airRequest._id, {
          monedaComparacion,
          tasas: opts.tasas ?? tasas,
        });
        if (r?.success) setData(r.data);
        else toast.error('No se pudo comparar', r?.message);
      } catch (e) {
        toast.error('No se pudo comparar', e?.message);
      } finally {
        setCargando(false);
      }
    },
    [airRequest, monedaComparacion, tasas, toast],
  );

  useEffect(() => {
    if (open) cargar();
    // Se recarga al abrir y al cambiar la moneda de comparación.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, monedaComparacion]);

  /** Monedas presentes en las ofertas que todavía no tienen tasa. */
  const monedasFaltantes = useMemo(() => {
    if (!data) return [];
    const s = new Set();
    for (const c of data.cotizaciones) {
      (c.monedasSinTasa || []).forEach((m) => s.add(m));
    }
    return [...s];
  }, [data]);

  const visibles = useMemo(
    () => (data?.cotizaciones || []).filter((c) => !excluidas.has(c.id)),
    [data, excluidas],
  );

  const filas = useMemo(() => {
    if (!data) return [];
    if (!soloDiferencias) return data.matrizConceptos;
    // Oculta las filas donde todas las ofertas visibles cobran lo mismo:
    // deja a la vista solo lo que realmente separa una oferta de otra.
    return data.matrizConceptos.filter((m) => {
      const vals = visibles.map((c) => {
        const v = m.valores[c.id];
        return v?.faltante ? 'falta' : (v?.totalComparable ?? null);
      });
      return new Set(vals.map(String)).size > 1;
    });
  }, [data, soloDiferencias, visibles]);

  const elegir = async (c) => {
    const r = await selectAirCotizacion(c.id);
    if (r?.success) {
      toast.success('Cotización seleccionada', c.numero);
      cargar();
      onChanged?.();
    } else {
      toast.error('No se pudo seleccionar', r?.message);
    }
  };

  if (!open) return null;

  const s = data?.solicitud;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange?.(false)}>
      {/*
        Usa el `Dialog` del proyecto, no un overlay propio: este modal se
        abre desde adentro del detalle de la solicitud, que es un
        `DialogContent` de Radix con `transform`. Un ancestro transformado
        pasa a ser el bloque contenedor de los `position: fixed` de adentro,
        así que `inset-0` medía contra esa tarjeta en vez de contra la
        pantalla y el `overflow-hidden` del padre recortaba encabezado y pie.
      */}
      <DialogContent
        hideClose
        className="flex h-[calc(100vh-2rem)] w-[95vw] max-w-none flex-col gap-0 overflow-hidden p-0"
      >
        {/* Encabezado */}
        <div className="flex shrink-0 items-start justify-between border-b p-4">
          <div>
            <h3 className="text-lg font-semibold">Comparar cotizaciones</h3>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <Plane className="h-3.5 w-3.5" />
              <span>{s?.codigo || airRequest?.codigo}</span>
              {s && (
                <>
                  <span>·</span>
                  <span>{s.numPiezas} piezas</span>
                  <span>·</span>
                  <span>{s.pesoTotal} kg brutos</span>
                  <span>·</span>
                  <span>{s.pesoVolumetrico} kg vol.</span>
                  <span>·</span>
                  <span className="font-medium text-foreground">
                    {s.pesoCobrable} kg cobrables
                  </span>
                </>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => onOpenChange?.(false)}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Controles */}
        <div className="flex shrink-0 flex-wrap items-end gap-3 border-b bg-muted/30 p-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Comparar en
            </label>
            <select
              className="h-8 rounded border border-input bg-background px-2 text-sm"
              value={monedaComparacion}
              onChange={(e) => setMonedaComparacion(e.target.value)}
            >
              {MONEDAS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          {monedasFaltantes.map((m) => (
            <div key={m} className="space-y-1">
              <label className="text-xs font-medium text-amber-700">
                Tasa {m} por 1 {monedaComparacion}
              </label>
              <div className="flex gap-1">
                <input
                  type="number"
                  step="any"
                  placeholder="3219.31"
                  className="h-8 w-32 rounded border border-amber-300 bg-background px-2 text-sm"
                  value={tasas[m] ?? ''}
                  onChange={(e) =>
                    setTasas((p) => ({
                      ...p,
                      [m]: e.target.value === '' ? undefined : Number(e.target.value),
                    }))
                  }
                />
                <Button size="sm" variant="outline" onClick={() => cargar()}>
                  Aplicar
                </Button>
              </div>
            </div>
          ))}

          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={soloDiferencias}
              onChange={(e) => setSoloDiferencias(e.target.checked)}
            />
            Ocultar conceptos donde todos cobran igual
          </label>
        </div>

        {/* Tabla */}
        <div className="min-h-0 flex-1 overflow-auto">
          {cargando && (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Comparando…
            </div>
          )}

          {!cargando && data && (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-background shadow-sm">
                <tr>
                  <th className="sticky left-0 z-20 min-w-[240px] bg-background px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                    Concepto
                  </th>
                  {visibles.map((c) => {
                    const esMejor = c.id === data.resumen.mejorTotalId;
                    const esRapida = c.id === data.resumen.mejorTransitoId;
                    const faltantes = (data.resumen.conceptosFaltantes[c.id] || []).length;
                    return (
                      <th
                        key={c.id}
                        className={cn(
                          'min-w-[150px] border-l px-3 py-2 text-left align-top',
                          esMejor && 'bg-emerald-50',
                          !c.comparable && 'bg-muted/50',
                        )}
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <span className="font-semibold">{c.numero}</span>
                            <button
                              type="button"
                              title="Quitar de la comparación"
                              className="text-muted-foreground hover:text-foreground"
                              onClick={() =>
                                setExcluidas((p) => new Set([...p, c.id]))
                              }
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                          <div className="text-[11px] font-normal text-muted-foreground">
                            {c.proveedor}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {esMejor && (
                              <Badge className="bg-emerald-600 text-[10px] hover:bg-emerald-600">
                                <Trophy className="mr-0.5 h-2.5 w-2.5" /> mejor total
                              </Badge>
                            )}
                            {esRapida && (
                              <Badge
                                variant="outline"
                                className="border-blue-300 bg-blue-50 text-[10px] font-normal text-blue-800"
                              >
                                <Zap className="mr-0.5 h-2.5 w-2.5" /> más rápida
                              </Badge>
                            )}
                            {!c.comparable && (
                              <Badge
                                variant="outline"
                                className="border-rose-300 bg-rose-50 text-[10px] font-normal text-rose-800"
                              >
                                sin tasa
                              </Badge>
                            )}
                            {c.vigente === false && (
                              <Badge
                                variant="outline"
                                className="border-rose-300 bg-rose-50 text-[10px] font-normal text-rose-800"
                              >
                                vencida
                              </Badge>
                            )}
                            {faltantes > 0 && (
                              <Badge
                                variant="outline"
                                className="border-amber-300 bg-amber-50 text-[10px] font-normal text-amber-800"
                                title={(data.resumen.conceptosFaltantes[c.id] || []).join(', ')}
                              >
                                <AlertTriangle className="mr-0.5 h-2.5 w-2.5" />
                                {faltantes} sin cotizar
                              </Badge>
                            )}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {(() => {
                  let grupo = null;
                  const out = [];
                  for (const m of filas) {
                    if (m.grupo !== grupo) {
                      grupo = m.grupo;
                      out.push(
                        <tr key={`g-${grupo}`} className="bg-muted/40">
                          <td
                            colSpan={visibles.length + 1}
                            className="sticky left-0 px-3 py-1.5 text-xs font-semibold"
                          >
                            {grupo}
                          </td>
                        </tr>,
                      );
                    }
                    out.push(
                      <tr key={m.clave} className="border-t">
                        <td className="sticky left-0 z-10 bg-background px-3 py-1.5">
                          <span>{m.nombre}</span>
                          {m.sinCatalogo && (
                            <Badge
                              variant="outline"
                              className="ml-1.5 text-[9px] font-normal text-muted-foreground"
                              title="No está en el catálogo: se comparó por el texto"
                            >
                              sin catalogar
                            </Badge>
                          )}
                        </td>
                        {visibles.map((c) => {
                          const v = m.valores[c.id];
                          const esMejor = m.mejorCotizacionId === c.id;
                          if (v?.faltante) {
                            return (
                              <td
                                key={c.id}
                                className="border-l bg-amber-50/60 px-3 py-1.5 text-xs text-amber-800"
                                title="Este proveedor no cotizó este concepto"
                              >
                                no cotizó
                              </td>
                            );
                          }
                          return (
                            <td
                              key={c.id}
                              className={cn(
                                'border-l px-3 py-1.5',
                                esMejor && 'bg-emerald-50 font-medium',
                              )}
                            >
                              <div>
                                {v?.totalComparable != null
                                  ? formatImporte(v.totalComparable)
                                  : '—'}
                              </div>
                              {v?.moneda && v.moneda !== data.monedaComparacion && (
                                <div className="text-[10px] text-muted-foreground">
                                  {formatImporte(v.total, v.moneda)}
                                </div>
                              )}
                              {v?.aplicoMinimo && (
                                <div className="text-[10px] text-amber-700">mínimo</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>,
                    );
                  }
                  return out;
                })()}
              </tbody>

              <tfoot className="sticky bottom-0 border-t-2 bg-background">
                <tr>
                  <td className="sticky left-0 bg-background px-3 py-2 font-semibold">
                    TOTAL ({data.monedaComparacion})
                  </td>
                  {visibles.map((c) => (
                    <td
                      key={c.id}
                      className={cn(
                        'border-l px-3 py-2 font-semibold',
                        c.id === data.resumen.mejorTotalId && 'bg-emerald-50 text-emerald-700',
                      )}
                    >
                      {c.totalComparable != null
                        ? formatImporte(c.totalComparable)
                        : 'no comparable'}
                    </td>
                  ))}
                </tr>
                <tr className="text-xs text-muted-foreground">
                  <td className="sticky left-0 bg-background px-3 py-1.5">
                    Tránsito · escalas · aerolínea
                  </td>
                  {visibles.map((c) => (
                    <td key={c.id} className="border-l px-3 py-1.5">
                      {[
                        c.diasTransito != null ? `${c.diasTransito} d` : null,
                        c.escalas != null
                          ? c.escalas === 0
                            ? 'directo'
                            : `${c.escalas} escala(s)`
                          : null,
                        c.aerolinea || null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="sticky left-0 bg-background px-3 py-2" />
                  {visibles.map((c) => (
                    <td key={c.id} className="border-l px-3 py-2">
                      {c.estado === 'Seleccionada' ? (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600">
                          <CheckCircle2 className="mr-1 h-3 w-3" /> Seleccionada
                        </Badge>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => elegir(c)}>
                          Seleccionar
                        </Button>
                      )}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Pie */}
        <div className="shrink-0 space-y-2 border-t p-3">
          {data?.resumen?.advertencias?.length > 0 && (
            <div className="space-y-1">
              {data.resumen.advertencias.map((a, i) => (
                <p
                  key={i}
                  className="flex items-start gap-1.5 text-xs text-amber-800"
                >
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  {a}
                </p>
              ))}
            </div>
          )}
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3 w-3 shrink-0" />
            Una celda en ámbar significa que ese proveedor <strong className="mx-1">
              no cotizó
            </strong>{' '}
            el concepto, no que sea gratis. Suele explicar por qué una oferta se ve más
            barata.
          </p>
          {excluidas.size > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setExcluidas(new Set())}
            >
              Volver a incluir las {excluidas.size} ocultas
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
